import type { Tool, User, Booking, Rating, DamageReport } from "./types";

type ToolWithOwner = Tool & { owner: User };
type BookingEnriched = Booking & { tool: Tool; borrower: User; lender: User };
type ToolDetail = Tool & { owner: User; bookings: Booking[] };

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
	const res = await fetch(url, {
		headers: { "Content-Type": "application/json" },
		...options,
	});
	if (!res.ok) {
		const error = await res.json().catch(() => ({ error: "Request failed" }));
		throw new Error(error.error || "Request failed");
	}
	return res.json();
}

export const api = {
	// User
	getMe: () => fetchJSON<User>("/api/me"),
	getUser: (id: string) => fetchJSON<User & { ratings: Rating[]; trustScore: number }>(`/api/users/${id}`),

	// Tools
	getTools: (params?: { q?: string; category?: string }) => {
		const searchParams = new URLSearchParams();
		if (params?.q) searchParams.set("q", params.q);
		if (params?.category) searchParams.set("category", params.category);
		const qs = searchParams.toString();
		return fetchJSON<ToolWithOwner[]>(`/api/tools${qs ? `?${qs}` : ""}`);
	},
	getTool: (id: string) => fetchJSON<ToolDetail>(`/api/tools/${id}`),
	createTool: (data: Partial<Tool>) =>
		fetchJSON<Tool>("/api/tools", { method: "POST", body: JSON.stringify(data) }),

	// Bookings
	getBookings: () => fetchJSON<BookingEnriched[]>("/api/bookings"),
	createBooking: (data: { toolId: string; startDate: string; endDate: string }) =>
		fetchJSON<Booking>("/api/bookings", { method: "POST", body: JSON.stringify(data) }),
	updateBookingStatus: (id: string, status: string) =>
		fetchJSON<Booking>(`/api/bookings/${id}/status`, {
			method: "PUT",
			body: JSON.stringify({ status }),
		}),

	// Ratings
	createRating: (data: { toUserId: string; bookingId: string; score: number; comment: string; type: string }) =>
		fetchJSON<Rating>("/api/ratings", { method: "POST", body: JSON.stringify(data) }),

	// Damage Reports
	createDamageReport: (data: {
		bookingId: string;
		description: string;
		severity: string;
		depositDeduction: number;
	}) =>
		fetchJSON<DamageReport>("/api/damage-reports", {
			method: "POST",
			body: JSON.stringify(data),
		}),
	updateDamageReportStatus: (id: string, status: string, resolution?: string) =>
		fetchJSON<DamageReport>(`/api/damage-reports/${id}/status`, {
			method: "PUT",
			body: JSON.stringify({ status, resolution }),
		}),
};
